import { useState } from "react";
import { FileText, Printer, Check } from "lucide-react";
import { useDashboard } from "@/components/layout/AppShell";
import { PageHeader, EmptyState } from "@/components/PageState";
import { SourceBadge } from "@/components/SourceBadge";
import { InsightPanel } from "@/components/InsightPanel";
import { OrbitWordmark } from "@/components/Brand";
import { trackEvent } from "@/lib/api";
import { formatDateLongIN } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "@/components/ui/sonner";

function Section({ n, title, badge, children }) {
  return (
    <section className="border-t border-border py-6 first:border-t-0">
      <div className="mb-3 flex items-center gap-3">
        <span className="font-num text-sm font-semibold text-accent">{n}</span>
        <h3 className="font-display text-lg font-semibold">{title}</h3>
        {badge && <SourceBadge type={badge} />}
      </div>
      <div className="pl-8 text-sm leading-relaxed text-muted-foreground">{children}</div>
    </section>
  );
}

export default function Reports() {
  const { data, loading } = useDashboard();
  const [generated, setGenerated] = useState(false);

  if (loading) return <Skeleton className="h-96 rounded-2xl" />;

  if (!data?.has_data) {
    return (
      <div>
        <PageHeader eyebrow="Outputs" title="Transformation Report" />
        <EmptyState icon={FileText} title="Your report compiles once you have results"
          whatsMissing="There are no scores, opportunities or roadmap to compile into a report yet."
          whyItMatters="The report is your shareable, consulting-grade deliverable — the artefact you take to your team." />
      </div>
    );
  }

  const s = data.scores;
  const generate = () => {
    setGenerated(true);
    trackEvent("REPORT_GENERATED");
    toast.success("Transformation report compiled");
  };

  if (!generated) {
    return (
      <div>
        <PageHeader eyebrow="Outputs" title="Transformation Report"
          description="A professional, consulting-grade deliverable assembled from your diagnosis — ready to share." />
        <div className="rounded-2xl border border-border bg-card p-8 text-center sm:p-14">
          <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-accent/10"><FileText className="h-7 w-7 text-accent" /></div>
          <h3 className="font-display text-xl font-semibold">Ready to compile your report</h3>
          <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">Thirteen sections spanning your profile, scores, opportunities, roadmap and clearly-labelled assumptions and risks.</p>
          <Button className="mt-6 bg-accent text-accent-foreground hover:bg-accent/90" onClick={generate} data-testid="generate-report-btn">Generate report</Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader eyebrow="Outputs" title="Transformation Report">
        <Button variant="outline" onClick={() => window.print()} data-testid="print-report-btn"><Printer className="h-4 w-4" /> Print / Save PDF</Button>
      </PageHeader>

      <div className="mb-6 print:hidden">
        <InsightPanel operation="score" payload={{ score_key: "digital_maturity" }} heading="Executive insight" variant="insight" testid="insight-report-executive" />
      </div>

      <div className="rounded-2xl border border-border bg-card p-6 sm:p-10">
        <div className="mb-6 flex items-center justify-between border-b border-border pb-6">
          <OrbitWordmark subtle />
          <div className="text-right text-xs text-muted-foreground">
            <p className="font-semibold text-foreground">{data.organisation_name}</p>
            <p>{formatDateLongIN()}</p>
            <p className="mt-0.5">MYTHIVA™ by Mythos India Studios Private Limited</p>
          </div>
        </div>

        <Section n="01" title="Executive Summary" badge="ai">
          {data.organisation_name} shows a Digital Maturity of <b className="text-foreground">{s.digital_maturity.score}/100 ({s.digital_maturity.level})</b> and
          AI Readiness of <b className="text-foreground">{s.ai_readiness.score}/100 ({s.ai_readiness.level})</b>.
          {data.opportunities?.length > 0 && <> The clearest near-term value lies in <b className="text-foreground">{data.opportunities[0].title.toLowerCase()}</b>{data.opportunities[1] ? <> and <b className="text-foreground">{data.opportunities[1].title.toLowerCase()}</b></> : null}, while foundational work prepares the business for later AI.</>} Any monetary impact requires validation against real operating data. [VERIFY]
        </Section>

        <Section n="02" title="Business Profile" badge="responses">
          <ul className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
            {Object.entries(data.profile || {}).map(([k, v]) => (
              <li key={k}><span className="capitalize text-foreground">{k.replace(/_/g, " ")}:</span> {v}</li>
            ))}
          </ul>
        </Section>

        <Section n="03" title="Digital Maturity" badge="calculated">
          {data.maturity_dimensions.map((d) => <span key={d.key} className="mr-4 inline-block">{d.label}: <b className="text-foreground font-num">{d.score}</b></span>)}
        </Section>

        <Section n="04" title="AI Readiness" badge="calculated">
          {data.ai_dimensions.map((d) => <span key={d.key} className="mr-4 inline-block">{d.label}: <b className="text-foreground font-num">{d.score}</b></span>)}
        </Section>

        <Section n="05" title="Business Strengths" badge="responses">
          <ul className="space-y-1">{data.strengths.map((t) => <li key={t} className="flex gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />{t}</li>)}</ul>
        </Section>

        <Section n="06" title="Transformation Gaps" badge="calculated">
          <ul className="list-inside list-disc space-y-1">{data.gaps.map((t) => <li key={t}>{t}</li>)}</ul>
        </Section>

        <Section n="07" title="Top Opportunities" badge="rule">
          <ol className="space-y-2">
            {data.opportunities.map((o) => (
              <li key={o.id}><b className="text-foreground">{o.title}</b> — Impact {o.impact}, Effort {o.effort}, Readiness {o.readiness} ({o.priority})</li>
            ))}
          </ol>
        </Section>

        <Section n="08" title="Impact / Effort Analysis" badge="rule">
          {(() => {
            const quick = data.opportunities.filter((o) => o.effort_score <= 2 && o.impact_score >= 2).map((o) => o.title);
            const strategic = data.opportunities.filter((o) => o.effort_score >= 3).map((o) => o.title);
            const later = data.opportunities.filter((o) => o.priority === "Later").map((o) => o.title);
            return <>Quick wins prioritise high-impact, lower-effort actions{quick.length ? <> ({quick.join(", ")})</> : null}. Strategic bets{strategic.length ? <> ({strategic.join(", ")})</> : null} require groundwork first{later.length ? <>, and higher-effort or lower-readiness items ({later.join(", ")}) are deliberately deferred</> : null}.</>;
          })()}
        </Section>

        <Section n="09" title="90-Day Roadmap" badge="rule">
          {data.roadmap.map((p) => (
            <div key={p.phase} className="mb-3">
              <p className="font-semibold text-foreground">{p.subtitle} — {p.title}</p>
              <ul className="list-inside list-disc">{p.items.map((it) => <li key={it.id}>{it.action} <span className="text-xs">({it.owner})</span></li>)}</ul>
            </div>
          ))}
        </Section>

        <Section n="10" title="Recommendations" badge="ai">
          Begin with the quick wins to build momentum and trust, run the data cleanup in parallel, and only then evaluate an AI pilot once readiness improves.
        </Section>

        <Section n="11" title="Assumptions" badge="verify">
          Figures are illustrative and assume broadly stable order volume and team availability. All value estimates require validation against real operating data before commitment.
        </Section>

        <Section n="12" title="Risks" badge="verify">
          Adoption risk (teams not using new tools), data-quality risk (automation on poor data), and over-investment risk (premature AI). Each is mitigated in the roadmap sequence.
        </Section>

        <Section n="13" title="Next Steps" badge="ai">
          Confirm the first three foundation actions and assign owners this week. When ready, MYTHIVA™ can help implement — request a consultation from any opportunity.
        </Section>
      </div>
    </div>
  );
}
